import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PermissionFormFields,
  REGISTRATION_STEPS,
  registrationChrome,
  registrationStepForField,
} from "../components/PermissionFormFields";
import { ErrorFlash } from "../components/ErrorFlash";
import { HintCallout } from "../components/HintCallout";
import { QuestionFill } from "../components/QuestionDesign";
import { FormFieldLabel } from "../components/FormFieldLabel";
import { PrototypeBanner, PublicNav } from "../components/ui";
import { normalizeEmail, studentIdsForAccount } from "../domain/auth";
import type { Student } from "../domain/data";
import { runtimeFromBrowser, sampleToolsEnabled } from "../domain/env";
import { formatPhoneInput } from "../domain/phone";
import {
  emptyPermission,
  normalizePermission,
  permissionCore,
  permissionIssue,
  type PermissionAnswers,
} from "../domain/permissionForm";
import {
  randomAnswerForQuestion,
  randomPermissionAnswers,
  sampleFillBeats,
  type SampleStep,
} from "../domain/randomStudent";
import { missingRequiredQuestion } from "../domain/questions";
import { useAuth } from "../state/AuthProvider";
import { defaultFormCopy, formCopyForLocale, useForms } from "../state/forms";
import { useLocale } from "../state/i18n";
import { useMail } from "../state/MailProvider";
import { useStudents } from "../state/students";

const FIELD_PAUSE_MS = 70;
const STEP_PAUSE_MS = 140;
const DRAFT_STORAGE_KEY = "lift-registration-draft-v1";

type RegistrationDraft = {
  permission: PermissionAnswers;
  extraAnswers: Record<string, string>;
  stepIndex: number;
  savedAt: number;
};

function readDraft(): RegistrationDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.permission) {
      return {
        permission: normalizePermission(parsed.permission),
        extraAnswers:
          parsed.extraAnswers && typeof parsed.extraAnswers === "object"
            ? (parsed.extraAnswers as Record<string, string>)
            : {},
        stepIndex:
          typeof parsed.stepIndex === "number"
            ? Math.max(0, Math.min(parsed.stepIndex, REGISTRATION_STEPS.length - 1))
            : 0,
        savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : Date.now(),
      };
    }
  } catch {
    /* ignore parse errors */
  }
  return null;
}

function clearDraft() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentForm, submitForm } = useForms();
  const { upsertStudent, studentsByIds, students } = useStudents();
  const { account, attachStudentToParent, ensureStudentAccount, saveParentPhone } =
    useAuth();
  const { notifyRegistration } = useMail();
  const { locale, t } = useLocale();
  const copy = formCopyForLocale(currentForm?.copy ?? defaultFormCopy(), locale);
  const isParent = account?.role === "parent";
  const siblings = isParent && account ? studentsByIds(studentIdsForAccount(account)) : [];

  const householdStudent = siblings[0]
    ?? (isParent && account
      ? students.find((item) => normalizeEmail(item.guardianEmail) === normalizeEmail(account.email))
      : undefined);

  const initialDraft = useRef(readDraft());
  const [draftRestored, setDraftRestored] = useState(() => Boolean(initialDraft.current));

  const [permission, setPermission] = useState<PermissionAnswers>(() => {
    if (initialDraft.current) return initialDraft.current.permission;
    return prefillPermission(emptyPermission(), account, householdStudent);
  });
  const [extraAnswers, setExtraAnswers] = useState<Record<string, string>>(() => {
    if (initialDraft.current) return initialDraft.current.extraAnswers;
    return {};
  });
  const [error, setError] = useState("");
  const [errorFieldId, setErrorFieldId] = useState("");
  const [stepIndex, setStepIndex] = useState(() => {
    if (initialDraft.current) return initialDraft.current.stepIndex;
    return 0;
  });
  const [filling, setFilling] = useState(false);
  const prefilledHousehold = useRef(false);
  const stepped = useRef(false);
  const lastStep = useRef(0);
  const fillingRef = useRef(false);
  const fillAbort = useRef<AbortController | null>(null);
  const runSampleRef = useRef<() => void>(() => {});
  const sampleTools = sampleToolsEnabled(runtimeFromBrowser().name);
  const fillRequested = Boolean(
    location.state &&
      typeof location.state === "object" &&
      (location.state as { fillSample?: boolean }).fillSample,
  );
  const step = REGISTRATION_STEPS[stepIndex] ?? "student";
  const chrome = registrationChrome(locale);

  useEffect(() => {
    if (initialDraft.current) return;
    if (prefilledHousehold.current || !householdStudent) return;
    prefilledHousehold.current = true;
    setPermission((current) => prefillPermission(current, account, householdStudent));
  }, [account, householdStudent]);

  useEffect(() => {
    if (fillingRef.current) return;
    const isDirty = Boolean(
      permission.student.firstName.trim() ||
      permission.student.lastName.trim() ||
      permission.guardian.phone.trim() ||
      stepIndex > 0
    );
    if (!isDirty) return;
    try {
      const payload: RegistrationDraft = {
        permission,
        extraAnswers,
        stepIndex,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota */
    }
  }, [permission, extraAnswers, stepIndex]);

  useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (fillingRef.current) return;
      const isDirty = Boolean(
        permission.student.firstName.trim() ||
        permission.student.lastName.trim() ||
        permission.guardian.phone.trim() ||
        stepIndex > 0
      );
      if (isDirty) {
        event.preventDefault();
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [permission, stepIndex]);

  useEffect(() => {
    return () => fillAbort.current?.abort();
  }, []);

  useEffect(() => {
    if (!fillRequested || !sampleTools || !currentForm) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    runSampleRef.current();
  }, [fillRequested, sampleTools, currentForm, navigate, location.pathname, location.search]);

  useEffect(() => {
    const stepChanged = lastStep.current !== stepIndex;
    lastStep.current = stepIndex;
    if (fillingRef.current) return;
    if (errorFieldId) {
      document.getElementById(errorFieldId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!stepped.current) {
      stepped.current = true;
      return;
    }
    if (!stepChanged) return;
    document.getElementById("reg-step-title")?.focus({ preventScroll: true });
    document.getElementById("reg-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [stepIndex, errorFieldId]);

  function clearError() {
    setError("");
    setErrorFieldId("");
  }

  function showIssue(fieldId: string, message: string, nextStep = stepIndex) {
    setError(message);
    setErrorFieldId(fieldId);
    if (nextStep !== stepIndex) setStepIndex(nextStep);
  }

  function issueOnOrBefore(index: number) {
    const issue = permissionIssue(permission);
    if (!issue) return null;
    const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
    if (issueIndex > index) return null;
    return issue;
  }

  function onContinue() {
    if (fillingRef.current) return;
    const issue = issueOnOrBefore(stepIndex);
    if (issue) {
      const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
      showIssue(issue.fieldId, locale === "es" ? issue.es : issue.en, issueIndex);
      return;
    }
    clearError();
    setStepIndex((current) => Math.min(current + 1, REGISTRATION_STEPS.length - 1));
  }

  function onBack() {
    if (fillingRef.current) return;
    clearError();
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  function onEditStep(next: (typeof REGISTRATION_STEPS)[number]) {
    if (fillingRef.current) return;
    clearError();
    setStepIndex(REGISTRATION_STEPS.indexOf(next));
  }

  async function saveRegistration(
    nextPermission: PermissionAnswers,
    nextExtra: Record<string, string>,
  ): Promise<boolean> {
    if (!isParent || !account) {
      setError(t("register.errorSignIn"));
      return false;
    }
    if (!currentForm) {
      setError(t("register.errorOpen"));
      return false;
    }
    const issue = permissionIssue(nextPermission);
    if (issue) {
      const issueIndex = REGISTRATION_STEPS.indexOf(registrationStepForField(issue.fieldId));
      showIssue(issue.fieldId, locale === "es" ? issue.es : issue.en, issueIndex);
      return false;
    }
    const missingExtra = missingRequiredQuestion(copy.extraQuestions ?? [], nextExtra);
    if (missingExtra) {
      setError(t("register.errorQuestion", { label: missingExtra.label }));
      setErrorFieldId(missingExtra.id);
      setStepIndex(REGISTRATION_STEPS.indexOf("review"));
      return false;
    }
    if (isParent && account && !account.phone?.trim() && nextPermission.guardian.phone.trim()) {
      const saved = await saveParentPhone(nextPermission.guardian.phone);
      if (!saved.ok) {
        setError(saved.error);
        return false;
      }
    }

    let student: Student;
    try {
      const core = permissionCore(nextPermission);
      student = await upsertStudent(core);
      await submitForm(currentForm.id, student.id, {
        ...core,
        permission: nextPermission,
        extraAnswers: nextExtra,
      });
    } catch (error) {
      setError(registrationSaveError(error, locale));
      return false;
    }
    ensureStudentAccount(student);
    attachStudentToParent(student.id);
    notifyRegistration(student, siblings.length > 0 ? "child_added" : "registered");
    clearDraft();
    setDraftRestored(false);
    navigate(`/register/done/${student.id}`);
    return true;
  }

  function onSubmit(event: FormEvent) {
    // Keep this from becoming a navigation. Saving happens only from the
    // review button: the Continue control sits in the same spot, and a native
    // submit on that click skips the review step.
    event.preventDefault();
  }

  function onReviewSubmit() {
    if (fillingRef.current) return;
    void saveRegistration(permission, extraAnswers);
  }

  async function onFillSample() {
    if (!currentForm) return;
    fillAbort.current?.abort();
    const controller = new AbortController();
    fillAbort.current = controller;
    fillingRef.current = true;
    setFilling(true);
    clearError();
    prefilledHousehold.current = true;
    try {
      const guardianPhone = account?.phone?.trim() || householdStudent?.guardianPhone;
      const complete = randomPermissionAnswers({
        guardian:
          account?.role === "parent"
            ? {
                firstName: account.given,
                lastName: account.family,
                email: account.email,
                phone: guardianPhone,
              }
            : undefined,
      });
      const beats = sampleFillBeats(complete);
      let shown: SampleStep | "" = "";
      setStepIndex(0);
      setPermission(emptyPermission());
      setExtraAnswers({});
      for (const beat of beats) {
        if (beat.step !== shown) {
          shown = beat.step;
          setStepIndex(REGISTRATION_STEPS.indexOf(beat.step));
          await wait(STEP_PAUSE_MS, controller.signal);
        }
        setPermission(beat.answers);
        await wait(30, controller.signal);
        markSampleField(beat.fieldId);
        await wait(FIELD_PAUSE_MS, controller.signal);
      }
      const extras = copy.extraQuestions ?? [];
      const answers: Record<string, string> = {};
      setStepIndex(REGISTRATION_STEPS.indexOf("review"));
      await wait(STEP_PAUSE_MS, controller.signal);
      for (const question of extras) {
        const value = randomAnswerForQuestion(question);
        if (!value) continue;
        answers[question.id] = value;
        setExtraAnswers({ ...answers });
        await wait(30, controller.signal);
        markSampleField(`${question.id}-field`);
        await wait(FIELD_PAUSE_MS, controller.signal);
      }
      clearSampleMark();
      if (controller.signal.aborted) return;
      setPermission(complete);
      setExtraAnswers(answers);
      setStepIndex(REGISTRATION_STEPS.indexOf("review"));
    } catch (error) {
      if (!isAbort(error)) {
        setError(registrationSaveError(error, locale));
      }
    } finally {
      if (fillAbort.current === controller) {
        fillingRef.current = false;
        setFilling(false);
        clearSampleMark();
        if (!controller.signal.aborted) {
          document.getElementById("reg-step-title")?.focus({ preventScroll: true });
          document.getElementById("reg-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  }

  runSampleRef.current = () => {
    void onFillSample();
  };

  const meta = siblings.length > 0 ? t("register.metaAdd") : t("register.metaFinish");
  const intro = copy.intro;

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell reg-page">
        <PublicNav />
        <div className="reg-wrap">
        <p className="meta">{meta}</p>
        <h1>{t("register.title")}</h1>
        <p className="lede">{intro}</p>

        {isParent && siblings.length > 0 ? (
          <HintCallout title={t("register.hintTitle")}>
            {t("register.hintAnotherStudent", {
              name: siblings.map((item) => item.given).join(", "),
            })}
          </HintCallout>
        ) : account && account.role !== "staff" && siblings.length === 0 ? (
          <HintCallout title={t("register.hintTitle")}>
            {t("register.hintFirstStudentBefore")}{" "}
            <strong className="hint-callout__email">{account.email}</strong>.{" "}
            {t("register.hintFirstStudentAfter")}
          </HintCallout>
        ) : null}

        {currentForm ? (
          <p className="reg-year">
            <span>{t("register.currentForm")}</span>
            <strong>{currentForm.name}</strong>
            <span>{t("register.opened", { date: currentForm.openedLabel })}</span>
          </p>
        ) : (
          <div className="flash flash--warning" role="status">
            {t("register.noForm")}
          </div>
        )}

        {sampleTools ? (
          <div className="qa-sample">
            <p className="qa-sample-tag">{t("sample.testOnly")}</p>
            <button type="button" onClick={() => void onFillSample()} disabled={filling || !currentForm}>
              {filling ? t("register.sampleBusy") : t("register.sampleFill")}
            </button>
            <p>{t("register.sampleNote")}</p>
          </div>
        ) : null}

        {draftRestored ? (
          <div
            className="flash flash--warning"
            role="status"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "0.5rem",
              margin: "1rem 0",
            }}
          >
            <span>
              {locale === "es"
                ? "Se ha restaurado su borrador en curso."
                : "Restored your registration draft."}
            </span>
            <button
              type="button"
              className="btn ghost"
              style={{
                padding: "0.2rem 0.6rem",
                fontSize: "0.85rem",
                background: "var(--bg)",
              }}
              onClick={() => {
                clearDraft();
                setDraftRestored(false);
                setPermission(prefillPermission(emptyPermission(), account, householdStudent));
                setExtraAnswers({});
                setStepIndex(0);
              }}
            >
              {locale === "es" ? "Empezar de nuevo" : "Start over"}
            </button>
          </div>
        ) : null}

        <form id="reg-form" className="reg-form" onSubmit={onSubmit} noValidate aria-busy={filling || undefined}>
          <nav className="reg-progress" aria-label={chrome.progress}>
            <ol>
              {REGISTRATION_STEPS.map((id, index) => (
                <li key={id}>
                  <button
                    type="button"
                    aria-current={id === step ? "step" : undefined}
                    disabled={filling || index >= stepIndex}
                    data-complete={index < stepIndex ? "true" : "false"}
                    onClick={() => onEditStep(id)}
                  >
                    <span className="reg-progress__num">{index + 1}</span>
                    <span className="reg-progress__label">{chrome.short[id]}</span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          {step !== "review" ? <p className="reg-required-note">{chrome.requiredNote}</p> : null}

          <PermissionFormFields
            value={permission}
            onChange={(next) => {
              if (fillingRef.current) return;
              setPermission(next);
              if (errorFieldId) clearError();
            }}
            step={step}
            errorFieldId={errorFieldId}
            errorMessage={error}
            onEditStep={onEditStep}
          />

          {step === "review" && copy.extraQuestions.length > 0 ? (
            <section className="reg-extra" aria-labelledby="reg-extra-title">
              <h2 id="reg-extra-title" className="reg-subhead">{t("register.moreQuestions")}</h2>
              {copy.extraQuestions.map((item) => (
                <div className={`field${errorFieldId === item.id ? " field--invalid" : ""}`} id={`${item.id}-field`} key={item.id}>
                  <FormFieldLabel htmlFor={item.id} required={item.required} optional={!item.required} presentation="asterisk">
                    {item.label}
                  </FormFieldLabel>
                  <QuestionFill
                    question={item}
                    value={extraAnswers[item.id] ?? ""}
                    onChange={(next) => {
                      if (fillingRef.current) return;
                      setExtraAnswers((current) => ({ ...current, [item.id]: next }));
                    }}
                  />
                  {errorFieldId === item.id ? <p className="field-error" id={`${item.id}-error`}>{error}</p> : null}
                </div>
              ))}
            </section>
          ) : null}

          {error ? <ErrorFlash key={error} message={error} /> : null}

          <div className="reg-nav">
            {stepIndex > 0 ? (
              <button type="button" className="reg-back" onClick={onBack} disabled={filling}>
                {chrome.back}
              </button>
            ) : <span />}
            {step === "review" ? (
              <button type="button" className="reg-submit" onClick={onReviewSubmit} disabled={!currentForm || filling}>
                {chrome.submit}
              </button>
            ) : (
              <button type="button" className="reg-next" onClick={onContinue} disabled={filling}>
                {chrome.continue}
              </button>
            )}
          </div>
        </form>
        </div>
      </main>
    </div>
  );
}

function prefillPermission(
  current: PermissionAnswers,
  account: { role: string; given: string; family: string; email: string; phone?: string } | null,
  household: Student | undefined,
): PermissionAnswers {
  const next: PermissionAnswers = {
    ...current,
    student: { ...current.student },
    guardian: { ...current.guardian },
    emergency1: { ...current.emergency1 },
  };
  if (account?.role === "parent") {
    if (!next.guardian.firstName) next.guardian.firstName = account.given;
    if (!next.guardian.lastName) next.guardian.lastName = account.family;
    if (!next.guardian.email) next.guardian.email = account.email;
    if (!next.guardian.phone && account.phone) {
      next.guardian.phone = formatPhoneInput(account.phone);
    }
  } else if (account) {
    if (!next.student.firstName) next.student.firstName = account.given;
    if (!next.student.lastName) next.student.lastName = account.family;
    if (!next.student.email) next.student.email = account.email;
  }
  if (!household) return next;
  if (!next.guardian.phone && household.guardianPhone) {
    next.guardian.phone = formatPhoneInput(household.guardianPhone);
  }
  if (!next.emergency1.firstName && household.emergencyName.trim()) {
    const [first, ...rest] = household.emergencyName.trim().split(/\s+/);
    next.emergency1.firstName = first ?? "";
    next.emergency1.lastName = rest.join(" ");
    next.emergency1.phone = formatPhoneInput(household.emergencyPhone);
  }
  return next;
}

function registrationSaveError(error: unknown, locale: "en" | "es"): string {
  const detail = error instanceof Error ? error.message : "";
  if (
    /sign in required|permission denied|row-level security|belong to your household|public registration cannot/i.test(
      detail,
    )
  ) {
    return locale === "es"
      ? "Inicie sesión para guardar este registro."
      : "Sign in to save this registration.";
  }
  if (detail) return detail;
  return locale === "es" ? "No se pudo guardar el formulario." : "Could not save that form.";
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(new DOMException("Aborted", "AbortError"));
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function markSampleField(fieldId: string) {
  clearSampleMark();
  const node = document.getElementById(fieldId);
  const field = node?.classList.contains("field") ? node : node?.closest(".field");
  field?.classList.add("field--sample");
  const behavior: ScrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
  (field ?? node)?.scrollIntoView({ behavior, block: "center" });
}

function clearSampleMark() {
  document.querySelectorAll(".field--sample").forEach((el) => {
    el.classList.remove("field--sample");
  });
}
